import { useState } from "react";
import { Link, useNavigate, useLocation } from "react-router-dom";
import { motion } from "framer-motion";
import { useAuth } from "@/context/AuthContext";
import { formatApiError } from "@/lib/api";
import { Loader2, Eye, EyeOff } from "lucide-react";

const Login = () => {
  const { login } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [showPw, setShowPw] = useState(false);
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);
    try {
      await login(email, password);
      navigate(location.state?.from || "/", { replace: true });
    } catch (err) {
      setError(formatApiError(err.response?.data?.detail) || err.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#030303] flex items-center justify-center px-4 relative overflow-hidden">
      <div className="absolute inset-0 opacity-30 bg-[radial-gradient(circle_at_30%_20%,rgba(0,240,255,0.15),transparent_40%)]" />
      <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} className="relative w-full max-w-md bg-[#0d0d0d] border border-white/10 rounded-2xl p-8">
        <Link to="/" className="font-heading font-extrabold text-2xl tracking-tighter text-white block mb-1">POWER<span className="text-[#00F0FF]">2000</span></Link>
        <p className="text-white/50 text-sm mb-8">Sign in to continue watching.</p>

        <form onSubmit={submit} className="space-y-4">
          <div>
            <label className="text-xs uppercase tracking-widest text-white/50">Email</label>
            <input type="email" required value={email} onChange={(e) => setEmail(e.target.value)} data-testid="login-email"
              className="mt-2 w-full bg-[#111111] border border-white/10 rounded-lg px-4 py-3 text-white focus:outline-none focus:ring-2 focus:ring-[#00F0FF]" placeholder="you@example.com" />
          </div>
          <div>
            <label className="text-xs uppercase tracking-widest text-white/50">Password</label>
            <div className="relative mt-2">
              <input type={showPw ? "text" : "password"} required value={password} onChange={(e) => setPassword(e.target.value)} data-testid="login-password"
                className="w-full bg-[#111111] border border-white/10 rounded-lg px-4 py-3 pr-12 text-white focus:outline-none focus:ring-2 focus:ring-[#00F0FF]" placeholder="••••••••" />
              <button type="button" onClick={() => setShowPw((s) => !s)} data-testid="toggle-password"
                className="absolute right-3 top-1/2 -translate-y-1/2 text-white/50 hover:text-white" aria-label={showPw ? "Hide password" : "Show password"}>
                {showPw ? <EyeOff size={18} /> : <Eye size={18} />}
              </button>
            </div>
          </div>

          {error && <p className="text-sm text-[#FF2A2A]" data-testid="login-error">{error}</p>}

          <button type="submit" disabled={loading} data-testid="login-submit"
            className="w-full rounded-full bg-[#00F0FF] text-black font-semibold py-3 hover:scale-[1.02] transition-transform disabled:opacity-60 flex items-center justify-center gap-2">
            {loading && <Loader2 size={18} className="animate-spin" />} Sign In
          </button>
        </form>

        <p className="text-sm text-white/50 mt-6">No account? <Link to="/signup" className="text-[#00F0FF] hover:underline">Create one</Link></p>
      </motion.div>
    </div>
  );
};

export default Login;
